import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GameCard, { Game } from 'components/gameCard/gameCard';
import cx from 'classnames';
import styles from './searchBar.module.scss';
import { gamesService } from 'api/games';

interface SearchBarProps {
  onClose?: () => void;
  games?: Game[];
}

const SearchBar: React.FC<SearchBarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(true); // Default to true when opened in layout header modal
  const [results, setResults] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced API Search logic for initial query page 1
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      setResults([]);
      setLoading(false);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalDocs(0);
      return;
    }

    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const response = await gamesService.searchGames(trimmed, 1, 16);
        if (response && response.data && response.data.docs) {
          const mapped = response.data.docs.map((g: any) => ({
            id: g.gameId,
            name: g.title,
            imgUrl: g.imageUrl,
            playingCount: 0,
            isFavorite: false,
          }));
          setResults(mapped);
          setTotalPages(response.data.totalPages || 1);
          setTotalDocs(response.data.totalDocs || 0);
          setCurrentPage(1);
        } else {
          setResults([]);
          setTotalPages(1);
          setTotalDocs(0);
          setCurrentPage(1);
        }
      } catch (err) {
        console.error('Failed to search games', err);
        setResults([]);
        setTotalPages(1);
        setTotalDocs(0);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Prevent body scrolling and clear search when search is unfocused
  useEffect(() => {
    if (isFocused) {
      document.body.style.overflow = 'hidden';
    } else {
      setQuery('');
      setResults([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalDocs(0);

      const otherModals = document.querySelectorAll(
        '[class*="modalOverlay"], [class*="searchBackdrop"]',
      );
      if (otherModals.length <= 1) {
        document.body.style.overflow = '';
      }
    }
    return () => {
      const otherModals = document.querySelectorAll(
        '[class*="modalOverlay"], [class*="searchBackdrop"]',
      );
      if (otherModals.length <= 1) {
        document.body.style.overflow = '';
      }
    };
  }, [isFocused]);

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
        if (onClose) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleClearOrClose = () => {
    if (query.length > 0) {
      setQuery('');
      setResults([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalDocs(0);
    } else {
      setIsFocused(false);
      if (onClose) onClose();
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || currentPage >= totalPages) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const trimmed = query.trim();

    try {
      const response = await gamesService.searchGames(trimmed, nextPage, 16);
      if (response && response.data && response.data.docs) {
        const mapped = response.data.docs.map((g: any) => ({
          id: g.gameId,
          name: g.title,
          imgUrl: g.imageUrl,
          playingCount: 0,
          isFavorite: false,
        }));
        setResults((prev) => [...prev, ...mapped]);
        setCurrentPage(nextPage);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
      }
    } catch (err) {
      console.error('Failed to load more search results', err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      {isFocused && <div className={styles.searchBackdrop} />}
      <div className={cx(styles.searchWrapper, { [styles.focused]: isFocused })} ref={containerRef}>
        <div className={`${styles.searchContainer} ${isFocused ? styles.focused : ''}`}>
          <span className={styles.searchIcon}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>

          <input
            type="search"
            className={styles.searchInput}
            name="search"
            placeholder={t('searchBar.placeholder', 'Search your game')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            autoFocus
          />

          {(isFocused || query.length > 0) && (
            <button
              className={styles.clearBtn}
              onClick={handleClearOrClose}
              aria-label={t('searchBar.clearOrClose', 'Clear or close search')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown Results Box */}
        {isFocused && query.trim().length > 0 && (
          <div className={styles.searchResultsBox}>
            {loading ? (
              <div
                style={{
                  color: '#7a9ab0',
                  padding: '32px 0',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {t('common.searching', 'Searching...')}
              </div>
            ) : results.length > 0 ? (
              <>
                <div className={styles.resultsGrid}>
                  {results.map((game, idx) => (
                    <div key={`${game.id}-${idx}`} className={styles.gridItem}>
                      <GameCard game={game} />
                    </div>
                  ))}
                </div>

                <div className={styles.loadMoreContainer}>
                  <div className={styles.viewedStats}>
                    {t('common.viewedStats', {
                      count: results.length,
                      total: totalDocs,
                      defaultValue: 'You viewed: {{count}} out of {{total}} Games',
                    })}
                  </div>
                  {currentPage < totalPages && (
                    <button
                      className={styles.loadMoreBtn}
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore
                        ? t('common.loading', 'Loading...')
                        : t('common.loadMore', 'Load more')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div
                style={{
                  color: '#7a9ab0',
                  padding: '32px 0',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {t('common.noResultsFound', 'No results found.')}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchBar;
